/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";

// ── Alternate "dual-plane" visualisation (concept) ───────────────────────────────
// Aircraft-display metaphor: a PLAN view (top, horizontal plane) + a PROFILE view
// (bottom, vertical plane), the two linked. Both SVGs share ONE horizontal x-window
// (the viewBox x-origin/width are identical), so a horizontal zoom/pan mirrors across
// both AND every risk's profile column lines up directly under its plan-view dot.
//
// Reuses the exact same radar-data each risk already carries — no API change.

const W = 660; // viewBox width — tightened so the radar fills the width (less dead-space each side)
const TOP_H = 320; // plan view (viewBox height) — cropped tighter so there's no blank band on top
const BOT_H = 200; // profile view (viewBox height)
const PLAN_VB_TOP = 10; // small top headroom in the plan viewBox (just clears the topmost labels)

// Plan-view geometry: a semicircle "sweep" with the viewer at bottom-centre.
const OX = W / 2;
const OY = 322; // fan apex (near the bottom of the taller viewBox)
const PLAN_R = 270; // bigger radar
const LABEL_R = PLAN_R + 16; // radius for the curved category labels (just outside the rim)
const SWEEP_START = Math.PI; // left edge — full semicircle, 180°
const SWEEP_SPAN = Math.PI; // to right edge — 0°, no margin at start/end

// Profile-view geometry: $0 line sits high (most action is downside/loss) so the empty
// gain band above it is small — keeps the two charts close together.
const CENTER_Y = 74; // $0 line
const GAIN_H = CENTER_Y - 20; // px available above $0
const LOSS_H = BOT_H - CENTER_Y - 12; // px available below $0

// Colour by signpost alert (position still conveys gain vs loss).
const ALERT_FILL = { red: "#dc2626", amber: "#d97706" };
const NEUTRAL = "#6b7280";
const alertColor = (a) => ALERT_FILL[a] || NEUTRAL;

// 3-D sphere gradients per alert colour (light highlight → base → shadow), for the dots/balls.
const SPHERE = {
  "#dc2626": { id: "sphere-red", light: "#fca5a5", mid: "#dc2626", dark: "#991b1b" },
  "#d97706": { id: "sphere-amber", light: "#fcd34d", mid: "#d97706", dark: "#92400e" },
  "#6b7280": { id: "sphere-grey", light: "#d1d5db", mid: "#6b7280", dark: "#374151" },
};
const sphereFill = (color) => (SPHERE[color] ? `url(#${SPHERE[color].id})` : color);

// A risk is "high velocity" if any of its velocity fields says high/fast. Velocity strings look
// like "Near Term + High". Only high-velocity risks get the motion "comet" on the radar.
function isHighVelocity(r) {
  const vals = [
    r?.velocity,
    r?.scenarios?.optimistic?.velocity,
    r?.scenarios?.base?.velocity,
    r?.scenarios?.pessimistic?.velocity,
  ];
  return vals.some((v) => /high|fast/i.test(String(v || "")));
}

function timeFrameR(tf) {
  if (!tf) return 0.65;
  const l = String(tf).toLowerCase();
  if (l.includes("near")) return 0.42;
  if (l.includes("long")) return 0.92;
  return 0.65;
}

// ID172 — radar ring radii for likelihood mode.
const LIKELIHOOD_OUTER = 0.95; // furthest-out band (e.g. "Highly Unlikely")
const LIKELIHOOD_INNER = 0.32; // nearest-centre band (e.g. "Highly Likely")

// Radial fraction for a likelihood band. The band list is ordered MOST-LIKELY → least-likely (e.g.
// "Almost Certain" is index 0), so the most-likely band sits nearest the CENTRE and the least-likely
// band on the OUTER ring. Falls back to mid-radius if unmatched. (Fix: likelihood was reversed on the
// radar — "Almost Certain" was landing on the outer ring.)
function likelihoodR(band, bands) {
  const list = Array.isArray(bands) ? bands : [];
  const n = list.length;
  if (n === 0) return 0.62;
  const idx = list.findIndex((b) => String(b).toLowerCase() === String(band || "").toLowerCase());
  if (idx < 0) return 0.62;
  if (n === 1) return (LIKELIHOOD_OUTER + LIKELIHOOD_INNER) / 2;
  return LIKELIHOOD_INNER + (idx / (n - 1)) * (LIKELIHOOD_OUTER - LIKELIHOOD_INNER);
}

// Minimum visible length (px) for a non-zero gain/loss segment, so small-value risks still
// show a line next to much larger ones (the shared $ axis would otherwise crush them to ~0).
const MIN_SEG = 16;

// Compact money label, e.g. 3_000_000 → "$3M".
function fmtMoney(v) {
  const n = Math.abs(Number(v) || 0);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

// Word-wrap a label into lines of at most ~maxChars (SVG <text> doesn't wrap on its own).
function wrapLabel(s, maxChars = 15) {
  const words = String(s || "")
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const test = cur ? `${cur} ${w}` : w;
    if (test.length > maxChars && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  });
  if (cur) lines.push(cur);
  return lines;
}

// Pan clamps — keep the (possibly zoomed) viewBox window inside the chart's content bounds so the
// radar can never be dragged/zoomed completely out of view. Content = the default viewBox extent.
const clampVx0 = (x, w) => Math.min(Math.max(x, 0), Math.max(0, W - w));
const clampPlanY = (y, h) =>
  Math.min(Math.max(y, PLAN_VB_TOP), Math.max(PLAN_VB_TOP, PLAN_VB_TOP + TOP_H - h));
const clampProfY = (y, h) => Math.min(Math.max(y, 0), Math.max(0, BOT_H - h));

// "Nice" round tick values from a step up to max — for the profile $ gridlines.
function niceTicks(max, count = 2) {
  if (!(max > 0)) return [];
  const rough = max / count;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  const niceStep = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const ticks = [];
  for (let v = niceStep; v <= max * 1.0001; v += niceStep) ticks.push(v);
  return ticks;
}

function DualPlaneRadar(
  {
    risks,
    taxonomy,
    selected,
    hovered,
    onSelect,
    onHover,
    plausibilityMode,
    likelihoodBands,
    consequenceLevels = [],
  },
  ref
) {
  // ID172 — in 'likelihood' mode the rings become configurable likelihood bands and the velocity
  // symbol is omitted; otherwise the radar uses the time-horizon rings + velocity comet.
  const isLikelihood = plausibilityMode === "likelihood";
  // Zoom model: a SHARED horizontal window (vx0, vw) drives both charts' x — so they stay
  // linked and each risk's profile column stays under its plan dot. Each chart has its OWN
  // vertical window (pY0/pH, fY0/fH) so a wheel zoom magnifies UNIFORMLY (x and y by the same
  // factor) toward the cursor, instead of stretching only sideways.
  const [vx0, setVx0] = useState(0);
  const [vw, setVw] = useState(W);
  const [pY0, setPY0] = useState(PLAN_VB_TOP);
  const [pH, setPH] = useState(TOP_H);
  const [fY0, setFY0] = useState(0);
  const [fH, setFH] = useState(BOT_H);
  const VW_MIN = W / 8; // up to 8× zoom
  const winRef = useRef({ vx0: 0, vw: W, pY0: PLAN_VB_TOP, pH: TOP_H, fY0: 0, fH: BOT_H });
  winRef.current = { vx0, vw, pY0, pH, fY0, fH };

  const planBoxRef = useRef(null);
  const profBoxRef = useRef(null);
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // { x, y } cursor pos (container-relative) for the hover card

  const activeId = selected || hovered;

  // ── Category spokes: all L1 categories (taxonomy ∪ risk categories) ──
  const categories = useMemo(() => {
    const tax = (taxonomy || []).map((t) => t.Category).filter(Boolean);
    const fromRisks = (risks || []).map((r) => r.category).filter(Boolean);
    return [...new Set([...tax, ...fromRisks])];
  }, [taxonomy, risks]);

  const catAngle = useCallback(
    (cat) => {
      const n = Math.max(categories.length, 1);
      const idx = categories.indexOf(cat);
      const i = idx < 0 ? 0 : idx;
      return SWEEP_START - ((i + 0.5) / n) * SWEEP_SPAN;
    },
    [categories]
  );

  // Max consequence across all scenarios → scales dot size and the profile $ axis.
  const maxVal = useMemo(() => {
    const vals = (risks || []).flatMap((r) => [
      r?.scenarios?.optimistic?.value || 0,
      r?.scenarios?.pessimistic?.value || 0,
    ]);
    return Math.max(...vals, 1);
  }, [risks]);

  // Profile $ gridline values (only meaningful when there's real quantitative data).
  const profileTicks = useMemo(() => (maxVal > 1000 ? niceTicks(maxVal, 2) : []), [maxVal]);

  // ── Per-risk derived model + L2 sub-wedge spokes (positions shared between the two charts) ──
  // Dots are always grouped into their L2 sub-wedge inside the parent L1 wedge. At 1× the
  // sub-wedges sit close to the L1 centre; zooming in magnifies the positions (markers stay a
  // constant size) so risks that share an L1 category fan apart by sub-category — the map
  // "suburbs become visible as you zoom" effect.
  const { model } = useMemo(() => {
    const n = Math.max(categories.length, 1);
    const wedge = SWEEP_SPAN / n; // angular width of one L1 category wedge

    // Group risks by L1 category, then by L2 sub-category.
    const byCat = {};
    (risks || []).forEach((r) => {
      const k = r.category || "—";
      (byCat[k] = byCat[k] || []).push(r);
    });

    // Fan each L1 wedge's distinct L2 sub-categories across the wedge.
    const subAngleOf = {}; // `${cat}|${sub}` -> angle
    const spokes = [];
    Object.entries(byCat).forEach(([cat, list]) => {
      const subs = [...new Set(list.map((r) => r.subCategory || "—"))];
      const ns = Math.max(subs.length, 1);
      const A1 = catAngle(cat === "—" ? undefined : cat);
      subs.forEach((sub, si) => {
        const subOffset = ns > 1 ? ((si + 0.5) / ns - 0.5) * wedge * 0.72 : 0;
        const angle = A1 + subOffset;
        subAngleOf[`${cat}|${sub}`] = angle;
        if (sub !== "—") spokes.push({ cat, sub, angle });
      });
    });

    const items = (risks || []).map((r) => {
      const cat = r.category || "—";
      const sub = r.subCategory || "—";
      const subList = byCat[cat].filter((x) => (x.subCategory || "—") === sub);
      const gi = subList.indexOf(r);
      const mid = (subList.length - 1) / 2;
      const baseAngle = subAngleOf[`${cat}|${sub}`] ?? catAngle(cat);
      const angle = baseAngle + (gi - mid) * 0.05; // small spread within a single sub-category
      const rawFrac = isLikelihood
        ? likelihoodR(r.likelihood, likelihoodBands)
        : timeFrameR(r.timeFrame);
      const rFrac = Math.max(0.18, Math.min(1.0, rawFrac));
      const planX = OX + rFrac * PLAN_R * Math.cos(angle);
      const planY = OY - rFrac * PLAN_R * Math.sin(angle);

      const opt = r?.scenarios?.optimistic?.value || 0;
      const base = r?.scenarios?.base?.value || 0;
      const pess = r?.scenarios?.pessimistic?.value || 0;
      const prox = r.signpostProximity ?? 0.5; // 1=optimistic pole, 0=pessimistic pole

      // Consequence value at the ball's current position → dot/ball size.
      let bv;
      if (base > 0) {
        bv =
          prox >= 0.5
            ? base + (opt - base) * ((prox - 0.5) / 0.5)
            : pess + (base - pess) * (prox / 0.5);
      } else {
        bv = opt * prox + pess * (1 - prox);
      }
      const ballR = Math.max(7, Math.min(20, 7 + (bv / maxVal) * 13));

      // Each pole sits on its OWN side of $0 by its threat/opportunity flag: a gain (opportunity)
      // above $0, a loss (threat) below. A threat risk legitimately marks BOTH the Best and Worst
      // scenarios as losses, so both poles belong below $0 — the ball then moves between a smaller
      // loss (best) and a bigger loss (worst) and never floats up into the gain band.
      const isOpp = (s) => String(s?.threatOpp || "").toLowerCase() === "opportunity";
      const optOpp = isOpp(r?.scenarios?.optimistic);
      const pessOpp = isOpp(r?.scenarios?.pessimistic);
      const rawPx = (v, up) => (v > 0 ? (v / maxVal) * (up ? GAIN_H : LOSS_H) : 0);
      let yOpt;
      let yPess;
      if (optOpp === pessOpp) {
        // Both poles the SAME side of $0. Order by magnitude (smaller nearer $0) and force a gap
        // that clears the ball, so the Best↔Worst column doesn't collapse behind the ball for a
        // small same-side risk (which is why AUD/USD looked like it had "no line").
        const up = optOpp;
        const band = up ? GAIN_H : LOSS_H;
        const rOpt = rawPx(opt, up);
        const rPess = rawPx(pess, up);
        const near = Math.max(Math.min(rOpt, rPess), MIN_SEG);
        const spread = Math.max(MIN_SEG, 2 * ballR + 8);
        const far = Math.min(band, Math.max(Math.max(rOpt, rPess), near + spread));
        const optNear = rOpt <= rPess;
        const offOpt = optNear ? near : far;
        const offPess = optNear ? far : near;
        yOpt = up ? CENTER_Y - offOpt : CENTER_Y + offOpt;
        yPess = up ? CENTER_Y - offPess : CENTER_Y + offPess;
      } else {
        // Opposite sides (best = gain, worst = loss): each at least MIN_SEG from $0 on its own side.
        const segPx = (v, up) => (v > 0 ? Math.max(rawPx(v, up), MIN_SEG) : 0);
        yOpt = optOpp ? CENTER_Y - segPx(opt, true) : CENTER_Y + segPx(opt, false);
        yPess = pessOpp ? CENTER_Y - segPx(pess, true) : CENTER_Y + segPx(pess, false);
      }
      // Column spans the full extent regardless of which pole is up/down; ball interpolates by prox.
      const yGain = Math.min(yOpt, yPess);
      const yLoss = Math.max(yOpt, yPess);
      const ballY = yPess + (yOpt - yPess) * prox;

      return {
        risk: r,
        x: planX, // shared x for plan dot AND profile column
        planY,
        ballR,
        yGain,
        yLoss,
        ballY,
        color: alertColor(r.signpostAlert),
        estVal: bv,
        // Velocity comet (high-velocity only): angle points from the dot to the radar centre. The two
        // views share the same vw + uniform scaling, so this content-space angle reads identically
        // on screen in both the Radar and Scenario views.
        // ID172 — no velocity comet in likelihood mode.
        highVel: !isLikelihood && isHighVelocity(r),
        velAngle: Math.atan2(OY - planY, OX - planX),
      };
    });
    return { model: items, l2Spokes: spokes };
  }, [risks, catAngle, categories, maxVal, isLikelihood, likelihoodBands]);

  // Uniform zoom toward a point. `chart` = which chart the cursor is over ('plan'|'profile');
  // that chart anchors on the cursor in BOTH axes, the other zooms about its own centre — so
  // the two stay horizontally linked while the whole thing magnifies toward the dot.
  const zoomAt = useCallback((rawFactor, chart, contentX, contentY) => {
    const s = winRef.current;
    const nw = Math.min(W, Math.max(VW_MIN, s.vw * rawFactor));
    const f = nw / s.vw; // effective factor after clamping (keeps x & y in lock-step)
    if (f === 1) return;

    // Horizontal (shared)
    const fx = s.vw ? (contentX - s.vx0) / s.vw : 0.5;
    const nx0 = contentX - fx * nw;

    // Vertical (per chart): hovered chart anchors on cursor-y, the other on its centre.
    const npH = s.pH * f;
    const nfH = s.fH * f;
    let npY0;
    let nfY0;
    if (chart === "plan") {
      const fy = s.pH ? (contentY - s.pY0) / s.pH : 0.5;
      npY0 = contentY - fy * npH;
      nfY0 = s.fY0 + s.fH / 2 - nfH / 2;
    } else {
      const fy = s.fH ? (contentY - s.fY0) / s.fH : 0.5;
      nfY0 = contentY - fy * nfH;
      npY0 = s.pY0 + s.pH / 2 - npH / 2;
    }
    setVw(nw);
    setVx0(clampVx0(nx0, nw));
    setPH(npH);
    setPY0(clampPlanY(npY0, npH));
    setFH(nfH);
    setFY0(clampProfY(nfY0, nfH));
  }, []);

  const resetView = () => {
    setVx0(0);
    setVw(W);
    setPY0(PLAN_VB_TOP);
    setPH(TOP_H);
    setFY0(0);
    setFH(BOT_H);
  };

  // Build the composed export SVG (both charts → one SVG, re-embedding the sphere gradients +
  // drop-shadow filter, numbering each sphere, with a numbered key below). Returns { svg, w, h }
  // or null if the charts aren't mounted yet.
  const buildExportSvg = useCallback(() => {
    const planSvg = planBoxRef.current?.querySelector("svg");
    const profSvg = profBoxRef.current?.querySelector("svg");
    if (!planSvg || !profSvg) return null;

    const esc = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const gradients = Object.values(SPHERE)
      .map(
        (s) =>
          `<radialGradient id="${s.id}" cx="35%" cy="28%" r="75%">` +
          `<stop offset="0%" stop-color="${s.light}"/>` +
          `<stop offset="55%" stop-color="${s.mid}"/>` +
          `<stop offset="100%" stop-color="${s.dark}"/></radialGradient>`
      )
      .join("");
    const filter =
      `<filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">` +
      `<feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000" flood-opacity="0.3"/></filter>`;
    const defs = `<defs>${gradients}${filter}</defs>`;

    const planVBnow = planSvg.getAttribute("viewBox");
    const profVBnow = profSvg.getAttribute("viewBox");
    const outW = 1100;
    const titleH = 40;
    const planH = Math.round((outW * winRef.current.pH) / winRef.current.vw);
    const profH = Math.round((outW * winRef.current.fH) / winRef.current.vw);

    // Number each sphere (same number on its plan dot AND its profile ball) — in content coords,
    // so it sits exactly on the dot inside the nested chart SVG.
    const numFont = (13 * winRef.current.vw) / outW;
    const numAt = (x, y, i) =>
      `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${numFont}" ` +
      `font-weight="700" fill="#ffffff" stroke="rgba(0,0,0,0.65)" stroke-width="${
        numFont * 0.16
      }" ` +
      `paint-order="stroke" text-anchor="middle" dominant-baseline="central">${i + 1}</text>`;
    const planNums = model.map((m, i) => numAt(m.x, m.planY, i)).join("");
    const profNums = model.map((m, i) => numAt(m.x, m.ballY, i)).join("");

    // Numbered key/description below the charts (2 columns once there are more than 10 risks).
    const cols = model.length > 10 ? 2 : 1;
    const rowsPerCol = Math.ceil(model.length / cols) || 0;
    const rowH = 18;
    const legPadTop = 14;
    const legHeaderH = 24;
    const legPadBottom = 16;
    const legendTop = titleH + planH + profH;
    const legendH = model.length ? legPadTop + legHeaderH + rowsPerCol * rowH + legPadBottom : 0;
    const totalH = legendTop + legendH;

    let legend = "";
    if (model.length) {
      const colW = outW / cols;
      const maxChars = Math.max(20, Math.floor((colW - 60) / 6.4));
      const rowsTop = legendTop + legPadTop + legHeaderH;
      legend =
        `<line x1="0" y1="${legendTop}" x2="${outW}" y2="${legendTop}" stroke="#e0e0e0" stroke-width="1"/>` +
        `<text x="20" y="${
          legendTop + legPadTop + 14
        }" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="#52009b">Key — numbered watchlist risks</text>`;
      legend += model
        .map((m, i) => {
          const col = Math.floor(i / rowsPerCol);
          const rowInCol = i % rowsPerCol;
          const x = 20 + col * colW;
          const y = rowsTop + rowInCol * rowH;
          const status =
            m.risk.signpostAlert === "red"
              ? "breached"
              : m.risk.signpostAlert === "amber"
              ? "approaching"
              : "on watch";
          const val = m.estVal > 0 ? ` · ~${fmtMoney(m.estVal)}` : " · qualitative";
          const cat = m.risk.category ? ` — ${m.risk.category}` : "";
          let line = `${i + 1}. ${m.risk.title || "Untitled"}${cat}${val} · ${status}`;
          if (line.length > maxChars) line = `${line.slice(0, maxChars - 1)}…`;
          return (
            `<circle cx="${x + 4}" cy="${y - 4}" r="4.5" fill="${m.color}"/>` +
            `<text x="${x + 14}" y="${y}" font-family="Arial, sans-serif" font-size="12.5" ` +
            `fill="#1f2937">${esc(line)}</text>`
          );
        })
        .join("");
    }

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${totalH}" viewBox="0 0 ${outW} ${totalH}">` +
      `<rect width="100%" height="100%" fill="#ffffff"/>${defs}` +
      `<text x="20" y="26" font-family="Arial, sans-serif" font-size="18" font-weight="700" fill="#52009b">Watchlist Risk Radar</text>` +
      `<svg x="0" y="${titleH}" width="${outW}" height="${planH}" viewBox="${planVBnow}" preserveAspectRatio="none">${planSvg.innerHTML}${planNums}</svg>` +
      `<svg x="0" y="${
        titleH + planH
      }" width="${outW}" height="${profH}" viewBox="${profVBnow}" preserveAspectRatio="none">${
        profSvg.innerHTML
      }${profNums}</svg>` +
      `${legend}</svg>`;

    return { svg, w: outW, h: totalH };
  }, [model]);

  // Rasterise an SVG string onto a 2× canvas → PNG Blob.
  const rasterize = useCallback(
    (svg, w, h) =>
      new Promise((resolve) => {
        const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
        const img = new Image();
        img.onload = () => {
          const scale = 2;
          const canvas = document.createElement("canvas");
          canvas.width = w * scale;
          canvas.height = h * scale;
          const ctx = canvas.getContext("2d");
          ctx.setTransform(scale, 0, 0, scale, 0, 0);
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          canvas.toBlob((b) => resolve(b), "image/png");
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      }),
    []
  );

  // Download the composed PNG (the on-screen "Export image" button).
  const exportImage = useCallback(async () => {
    const built = buildExportSvg();
    if (!built) return;
    const blob = await rasterize(built.svg, built.w, built.h);
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "watchlist-risk-radar.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }, [buildExportSvg, rasterize]);

  // Imperative API for headless capture (e.g. embedding the radar in the board paper) — returns the
  // PNG as raw base64 (no data: prefix), or null if the chart isn't ready.
  useImperativeHandle(
    ref,
    () => ({
      getPngBase64: async () => {
        const built = buildExportSvg();
        if (!built) return null;
        const blob = await rasterize(built.svg, built.w, built.h);
        if (!blob) return null;
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => resolve("");
          reader.readAsDataURL(blob);
        });
        return dataUrl.includes(",") ? dataUrl.split(",")[1] : null;
      },
    }),
    [buildExportSvg, rasterize]
  );

  // Native non-passive wheel listeners on both chart boxes (zoom from either, toward the cursor).
  useEffect(() => {
    const attach = (box, chart) => {
      if (!box) return undefined;
      const onWheel = (e) => {
        e.preventDefault();
        const rect = box.getBoundingClientRect();
        const s = winRef.current;
        const cx = s.vx0 + ((e.clientX - rect.left) / rect.width) * s.vw;
        const y0 = chart === "plan" ? s.pY0 : s.fY0;
        const hh = chart === "plan" ? s.pH : s.fH;
        const cy = y0 + ((e.clientY - rect.top) / rect.height) * hh;
        zoomAt(e.deltaY < 0 ? 1 / 1.3 : 1.3, chart, cx, cy);
      };
      box.addEventListener("wheel", onWheel, { passive: false });
      return () => box.removeEventListener("wheel", onWheel);
    };
    const d1 = attach(planBoxRef.current, "plan");
    const d2 = attach(profBoxRef.current, "profile");
    return () => {
      if (d1) d1();
      if (d2) d2();
    };
  }, [zoomAt]);

  // ── Click-and-drag to pan ──
  // Horizontal drag moves the SHARED x-window (both charts pan together, staying linked);
  // vertical drag moves only the chart you grabbed. Lets you recover after a zoom drifts.
  const dragRef = useRef({ active: false });
  const [dragging, setDragging] = useState(false);
  const onDragStart = (e, chart) => {
    const box = chart === "plan" ? planBoxRef.current : profBoxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const s = winRef.current;
    dragRef.current = {
      active: true,
      chart,
      sx: e.clientX,
      sy: e.clientY,
      sVx0: s.vx0,
      sY0: chart === "plan" ? s.pY0 : s.fY0,
      rectW: rect.width,
      rectH: rect.height,
      vw: s.vw,
      chartH: chart === "plan" ? s.pH : s.fH,
    };
    setDragging(true);
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = ((e.clientX - d.sx) / d.rectW) * d.vw || 0;
    const dy = ((e.clientY - d.sy) / d.rectH) * d.chartH || 0;
    setVx0(clampVx0(d.sVx0 - dx, d.vw));
    if (d.chart === "plan") setPY0(clampPlanY(d.sY0 - dy, d.chartH));
    else setFY0(clampProfY(d.sY0 - dy, d.chartH));
  };
  const onDragEnd = () => {
    if (dragRef.current.active) {
      dragRef.current.active = false;
      setDragging(false);
    }
  };

  const inWin = (x, m = 12) => x >= vx0 - m && x <= vx0 + vw + m;

  const planVB = `${vx0} ${pY0} ${vw} ${pH}`;
  const profVB = `${vx0} ${fY0} ${vw} ${fH}`;

  // Map-style zoom: the viewBox shrink magnifies POSITIONS so dots spread apart, but markers and
  // text must stay a constant on-screen size. `zk` (= viewBox width / full width, ≤ 1 when zoomed
  // in) is the counter-scale: multiply every dot radius / font-size / size-offset by zk so it
  // renders at the same pixel size at any zoom. Strokes use vector-effect="non-scaling-stroke".
  const zk = vw / W;

  // Velocity "force" streaks: 3 PARALLEL speed lines that sit BEHIND the sphere and align to the
  // dot→radar-centre axis (so they point toward the centre of the radar, like a force pushing the
  // dot inward). `a` points to the centre; the lines lie on the trailing side. The centre line is
  // longer and reaches further behind. Drawn before the dot so they tuck behind it. zk = const size.
  const velocityComet = (cx, cy, a, ballR, idKey) => {
    const dx = Math.cos(a); // toward the radar centre
    const dy = Math.sin(a);
    const px = -dy; // perpendicular (for the 3 parallel offsets)
    const py = dx;
    // 3 streaks → reads as a gush of wind / a ball coming at speed. Fixed spacing so the gush looks
    // the same density on every ball, regardless of its size.
    const gap = 3.6 * zk; // perpendicular spread (constant, not tied to ball size)
    const baseNear = (ballR + 5) * zk; // clear gap between the ball edge and the streaks
    const maxLen = 19 * zk; // longest streak — also the span of the fade gradient
    // Each streak fades from transparent (tail, far from the ball) to solid motion-blue at the head
    // (near the ball), so it reads as speed rather than a hard line. One gradient per comet, oriented
    // along the toward-centre axis in user space so all three parallel streaks fade together.
    const gradId = `velgrad-${idKey}`;
    const tailX = cx - dx * (baseNear + maxLen);
    const tailY = cy - dy * (baseNear + maxLen);
    const headX = cx - dx * baseNear;
    const headY = cy - dy * baseNear;
    return (
      <g key={`vel-${idKey}`}>
        <defs>
          <linearGradient
            id={gradId}
            gradientUnits="userSpaceOnUse"
            x1={tailX}
            y1={tailY}
            x2={headX}
            y2={headY}
          >
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0" />
            <stop offset="45%" stopColor="#3b6ef5" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        {[-1, 0, 1].map((k) => {
          const ak = Math.abs(k);
          const ox = px * gap * k;
          const oy = py * gap * k;
          const len = (14 + ak * 3) * zk; // outer streaks longer
          const near = baseNear + (ak === 0 ? 4 : ak === 1 ? 1.5 : 0) * zk; // middle staggered back
          // Line lies on the trailing side: from far-behind (−dir·(near+len)) to its near end (−dir·near),
          // all parallel to the toward-centre axis.
          return (
            <line
              key={`vel-${idKey}-${k}`}
              x1={cx - dx * (near + len) + ox}
              y1={cy - dy * (near + len) + oy}
              x2={cx - dx * near + ox}
              y2={cy - dy * near + oy}
              stroke={`url(#${gradId})`}
              strokeWidth={ak === 0 ? 2.4 : 2}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </g>
    );
  };

  // Click-to-focus: when a dot is selected (clicked), HIGHLIGHT it on both charts (and show its
  // detail in the side panel) while keeping every other risk on screen — just greyed out and
  // subtle, so the selection stands out without losing context. Clicking it again clears
  // `selected` (handled in the parent) → everything returns to full colour.
  // All risks and all category spokes always render; selection only changes emphasis.
  const visibleModel = model;
  const visibleCategories = categories;
  // True for a non-selected risk while something is selected → render it dimmed/grey.
  const isDimmed = (id) => !!selected && selected !== id;

  const showTip = (e) => {
    const r = containerRef.current && containerRef.current.getBoundingClientRect();
    if (r) setTip({ x: e.clientX - r.left, y: e.clientY - r.top });
  };
  const tipRisk = hovered ? model.find((m) => m.risk.id === hovered) : null;

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      {/* Shared gradient + shadow defs for the 3-D dots */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          {Object.values(SPHERE).map((s) => (
            <radialGradient key={s.id} id={s.id} cx="35%" cy="28%" r="75%">
              <stop offset="0%" stopColor={s.light} />
              <stop offset="55%" stopColor={s.mid} />
              <stop offset="100%" stopColor={s.dark} />
            </radialGradient>
          ))}
          <filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#000" floodOpacity="0.3" />
          </filter>
        </defs>
      </svg>
      {/* Toolbar — floated over the chart's top-right so it doesn't push the chart down
          (keeps the plan view's top edge in line with the right-side detail card). */}
      <Box
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          zIndex: 5,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          bgcolor: "rgba(255,255,255,0.85)",
          borderRadius: "6px",
          px: 0.5,
        }}
      >
        <MDButton
          size="small"
          variant="outlined"
          onClick={exportImage}
          sx={{
            color: "#52009b",
            borderColor: "#52009b",
            textTransform: "none",
            "&:hover": { borderColor: "#3a0070", bgcolor: "rgba(82,0,155,0.06)" },
          }}
        >
          Export image
        </MDButton>
        {/* Reset sits to the LEFT of the zoom box so the −/+ buttons stay anchored at the right edge
            and never move when Reset appears (otherwise a second zoom click lands on Reset). */}
        {vw !== W && (
          <MDButton
            size="small"
            variant="text"
            onClick={resetView}
            sx={{ color: "#52009b", textTransform: "none", fontSize: "0.7rem", minWidth: 0 }}
          >
            Reset
          </MDButton>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid rgba(82,0,155,0.3)",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => zoomAt(1.4, "plan", vx0 + vw / 2, pY0 + pH / 2)}
            sx={{ px: 1.25, py: 0.5, cursor: "pointer", color: "#52009b", fontWeight: 700 }}
          >
            −
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              fontSize: "0.72rem",
              color: "#52009b",
              minWidth: 44,
              textAlign: "center",
            }}
          >
            {Math.round((W / vw) * 100)}%
          </Box>
          <Box
            onClick={() => zoomAt(1 / 1.4, "plan", vx0 + vw / 2, pY0 + pH / 2)}
            sx={{ px: 1.25, py: 0.5, cursor: "pointer", color: "#52009b", fontWeight: 700 }}
          >
            +
          </Box>
        </Box>
      </Box>

      {/* ── PLAN VIEW (top, horizontal plane) ── */}
      <Box
        ref={planBoxRef}
        onMouseDown={(e) => onDragStart(e, "plan")}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        sx={{
          border: "1px solid rgba(82,0,155,0.15)",
          borderBottom: "none",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          bgcolor: "#fff",
          userSelect: "none",
          overflow: "hidden",
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        <svg viewBox={planVB} style={{ width: "100%", display: "block" }} aria-label="Plan view">
          <text
            x={vx0 + 8 * zk}
            y={pY0 + 14 * zk}
            fontSize={10 * zk}
            fontWeight={700}
            fill="rgba(82,0,155,0.55)"
          >
            Radar View
          </text>

          {/* Alternating category-wedge shading for a structured radar look */}
          {categories.map((cat, i) => {
            if (i % 2 !== 0) return null;
            const n = Math.max(categories.length, 1);
            const a0 = SWEEP_START - (i / n) * SWEEP_SPAN;
            const a1 = SWEEP_START - ((i + 1) / n) * SWEEP_SPAN;
            const p0x = OX + PLAN_R * Math.cos(a0);
            const p0y = OY - PLAN_R * Math.sin(a0);
            const p1x = OX + PLAN_R * Math.cos(a1);
            const p1y = OY - PLAN_R * Math.sin(a1);
            return (
              <path
                key={`wedge-${cat}`}
                d={`M ${OX} ${OY} L ${p0x} ${p0y} A ${PLAN_R} ${PLAN_R} 0 0 1 ${p1x} ${p1y} Z`}
                fill="rgba(82,0,155,0.035)"
              />
            );
          })}

          {/* Range rings — likelihood bands (ID172) or Near/Medium/Long time-horizon rings */}
          {(isLikelihood
            ? (likelihoodBands || []).map((label, i, arr) => ({
                rf: likelihoodR(label, arr),
                label,
              }))
            : [
                { rf: 0.42, label: "Near" },
                { rf: 0.65, label: "Medium" },
                { rf: 0.92, label: "Long" },
              ]
          ).map(({ rf, label }) => (
            <g key={`${label}-${rf}`}>
              <path
                d={`M ${OX - rf * PLAN_R} ${OY} A ${rf * PLAN_R} ${rf * PLAN_R} 0 0 1 ${
                  OX + rf * PLAN_R
                } ${OY}`}
                fill="none"
                stroke="rgba(82,0,155,0.12)"
                strokeWidth={1}
                strokeDasharray={rf < 0.92 ? "5 4" : "none"}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={OX}
                y={OY - rf * PLAN_R - 3 * zk}
                fontSize={8 * zk}
                fill="rgba(82,0,155,0.4)"
                textAnchor="middle"
                style={{ pointerEvents: "none" }}
              >
                {label}
              </text>
            </g>
          ))}

          {/* Category spokes + curved labels: each label sits at the CENTRE of its wedge, hugs the
              rim and is oriented tangentially so it runs AROUND the radar (not sticking out) and
              isn't clipped. Long names wrap onto stacked lines so neighbours don't overlap. */}
          {visibleCategories.map((cat) => {
            const a = catAngle(cat);
            const r = LABEL_R; // all labels on one even arc (wrapping handles crowding)
            const lx = OX + r * Math.cos(a);
            const ly = OY - r * Math.sin(a);
            // Tangent orientation (runs along the arc), normalised to stay upright.
            let rot = (-a * 180) / Math.PI - 90;
            while (rot > 90) rot -= 180;
            while (rot < -90) rot += 180;
            return (
              <g key={cat}>
                {/* L1 wedge centre spoke removed per request — labels only. */}
                {inWin(lx) &&
                  (() => {
                    const lines = wrapLabel(cat, 15);
                    const lh = 9 * zk; // line height (constant on-screen)
                    return (
                      <text
                        x={lx}
                        y={ly}
                        fontSize={9 * zk}
                        fontWeight={700}
                        fill="#52009b"
                        textAnchor="middle"
                        transform={`rotate(${rot} ${lx} ${ly})`}
                      >
                        {lines.map((ln, i) => (
                          <tspan
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            x={lx}
                            dy={i === 0 ? -((lines.length - 1) * lh) / 2 : lh}
                          >
                            {ln}
                          </tspan>
                        ))}
                      </text>
                    );
                  })()}
              </g>
            );
          })}

          {/* L2 sub-category visuals (spokes + labels) removed for now — to be reintroduced later. */}

          {/* Velocity streaks — drawn as ONE layer beneath every dot so they sit behind ALL balls. */}
          {visibleModel.map((m) =>
            m.highVel && !isDimmed(m.risk.id)
              ? velocityComet(m.x, m.planY, m.velAngle, m.ballR, `pl-${m.risk.id}`)
              : null
          )}

          {/* Risk dots (size = consequence, colour = alert, 3-D sphere shading) */}
          {visibleModel.map((m) => {
            const isActive = activeId === m.risk.id;
            const dim = isDimmed(m.risk.id);
            return (
              <g
                key={m.risk.id}
                style={{ cursor: "pointer" }}
                opacity={dim ? 0.32 : 1}
                onMouseEnter={(e) => {
                  onHover(m.risk.id);
                  showTip(e);
                }}
                onMouseMove={showTip}
                onMouseLeave={() => onHover(null)}
                onClick={() => onSelect(m.risk)}
              >
                {isActive && !dim && (
                  <circle
                    cx={m.x}
                    cy={m.planY}
                    r={(m.ballR + 6) * zk}
                    fill={m.color}
                    opacity={0.18}
                  />
                )}
                <circle
                  cx={m.x}
                  cy={m.planY}
                  r={m.ballR * zk}
                  fill={dim ? sphereFill(NEUTRAL) : sphereFill(m.color)}
                  stroke="#fff"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  filter={dim ? undefined : "url(#dotShadow)"}
                />
                {/* Risk title is shown in the side detail panel on hover/select, not on the chart. */}
              </g>
            );
          })}

          {/* Link line to the profile column for the active risk */}
          {activeId &&
            model
              .filter((m) => m.risk.id === activeId)
              .map((m) => (
                <line
                  key={`pl-${m.risk.id}`}
                  x1={m.x}
                  y1={m.planY}
                  x2={m.x}
                  y2={TOP_H}
                  stroke={m.color}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  opacity={0.5}
                  vectorEffect="non-scaling-stroke"
                />
              ))}

          {/* Viewer marker */}
          <circle cx={OX} cy={OY} r={3 * zk} fill="rgba(82,0,155,0.4)" />
        </svg>
      </Box>

      {/* ── PROFILE VIEW (bottom, vertical plane) ── */}
      <Box
        ref={profBoxRef}
        onMouseDown={(e) => onDragStart(e, "profile")}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        sx={{
          border: "1px solid rgba(82,0,155,0.15)",
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          bgcolor: "#fff",
          userSelect: "none",
          overflow: "hidden",
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        <svg viewBox={profVB} style={{ width: "100%", display: "block" }} aria-label="Profile view">
          {/* Gain (green) / loss (red) zone tint */}
          <rect
            x={vx0}
            y={fY0}
            width={vw}
            height={Math.max(0, CENTER_Y - fY0)}
            fill="rgba(34,197,94,0.06)"
          />
          <rect
            x={vx0}
            y={CENTER_Y}
            width={vw}
            height={Math.max(0, fY0 + fH - CENTER_Y)}
            fill="rgba(239,68,68,0.06)"
          />
          <text
            x={vx0 + 8 * zk}
            y={fY0 + 12 * zk}
            fontSize={10 * zk}
            fontWeight={700}
            fill="rgba(82,0,155,0.55)"
          >
            Scenario View — gain ▲ / loss ▼
          </text>
          {/* Axis orientation: Financial ($) on the left, qualitative Level on the right. */}
          <text
            x={vx0 + vw - 8 * zk}
            y={fY0 + 12 * zk}
            fontSize={9 * zk}
            fontWeight={700}
            fill="rgba(82,0,155,0.45)"
            textAnchor="end"
          >
            Financial (left) · Level (right)
          </text>

          {/* $0 centreline */}
          <line
            x1={vx0}
            y1={CENTER_Y}
            x2={vx0 + vw}
            y2={CENTER_Y}
            stroke="rgba(0,0,0,0.45)"
            strokeWidth={1.25}
            vectorEffect="non-scaling-stroke"
          />
          <text x={vx0 + 6 * zk} y={CENTER_Y - 4 * zk} fontSize={10 * zk} fill="rgba(0,0,0,0.5)">
            $0
          </text>

          {/* Gain (up, green) / Loss (down, red) direction arrows flanking the $0 axis.
              Scaled about the left-axis anchor so the arrows stay a constant on-screen size. */}
          <g
            style={{ pointerEvents: "none" }}
            transform={`translate(${vx0} ${CENTER_Y}) scale(${zk}) translate(${-vx0} ${-CENTER_Y})`}
          >
            <line
              x1={vx0 + 28}
              y1={CENTER_Y - 6}
              x2={vx0 + 28}
              y2={CENTER_Y - 26}
              stroke="#16a34a"
              strokeWidth={1.5}
            />
            <polygon
              points={`${vx0 + 28},${CENTER_Y - 31} ${vx0 + 24},${CENTER_Y - 23} ${vx0 + 32},${
                CENTER_Y - 23
              }`}
              fill="#16a34a"
            />
            <text x={vx0 + 36} y={CENTER_Y - 20} fontSize={9} fontWeight={700} fill="#16a34a">
              Gain
            </text>
            <line
              x1={vx0 + 28}
              y1={CENTER_Y + 6}
              x2={vx0 + 28}
              y2={CENTER_Y + 26}
              stroke="#dc2626"
              strokeWidth={1.5}
            />
            <polygon
              points={`${vx0 + 28},${CENTER_Y + 31} ${vx0 + 24},${CENTER_Y + 23} ${vx0 + 32},${
                CENTER_Y + 23
              }`}
              fill="#dc2626"
            />
            <text x={vx0 + 36} y={CENTER_Y + 32} fontSize={9} fontWeight={700} fill="#dc2626">
              Loss
            </text>
          </g>

          {/* Quantitative $ (Financial) gridlines — gain above $0, loss below (labels on the LEFT) */}
          {profileTicks.map((v) => {
            const yG = CENTER_Y - (v / maxVal) * GAIN_H;
            const yL = CENTER_Y + (v / maxVal) * LOSS_H;
            return (
              <g key={v}>
                <line
                  x1={vx0}
                  y1={yG}
                  x2={vx0 + vw}
                  y2={yG}
                  stroke="rgba(0,0,0,0.2)"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={vx0 + 6 * zk}
                  y={yG - 2 * zk}
                  fontSize={8 * zk}
                  fill="rgba(0,0,0,0.4)"
                  textAnchor="start"
                >
                  +{fmtMoney(v)}
                </text>
                <line
                  x1={vx0}
                  y1={yL}
                  x2={vx0 + vw}
                  y2={yL}
                  stroke="rgba(0,0,0,0.2)"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={vx0 + 6 * zk}
                  y={yL - 2 * zk}
                  fontSize={8 * zk}
                  fill="rgba(0,0,0,0.4)"
                  textAnchor="start"
                >
                  −{fmtMoney(v)}
                </text>
              </g>
            );
          })}

          {/* Qualitative "Level" reference on the RIGHT — the consequence levels placed from least
              severe (near $0) to most severe (at the extremes), on both the gain and loss halves.
              Indicative placement (levels aren't linear in $), opposite the Financial scale. */}
          {consequenceLevels.map((lvl, i) => {
            const frac = (i + 1) / consequenceLevels.length;
            const yG = CENTER_Y - frac * GAIN_H;
            const yL = CENTER_Y + frac * LOSS_H;
            return (
              <g key={lvl}>
                <text
                  x={vx0 + vw - 6 * zk}
                  y={yG - 2 * zk}
                  fontSize={8 * zk}
                  fill="rgba(82,0,155,0.45)"
                  textAnchor="end"
                >
                  {lvl}
                </text>
                <text
                  x={vx0 + vw - 6 * zk}
                  y={yL - 2 * zk}
                  fontSize={8 * zk}
                  fill="rgba(82,0,155,0.45)"
                  textAnchor="end"
                >
                  {lvl}
                </text>
              </g>
            );
          })}

          {/* Active-risk link line (aligns under the plan dot) */}
          {activeId &&
            model
              .filter((m) => m.risk.id === activeId)
              .map((m) => (
                <line
                  key={`pf-${m.risk.id}`}
                  x1={m.x}
                  y1={0}
                  x2={m.x}
                  y2={BOT_H}
                  stroke={m.color}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  opacity={0.4}
                  vectorEffect="non-scaling-stroke"
                />
              ))}

          {/* Risk columns: gain→loss line + current ball (3-D sphere) */}
          {visibleModel.map((m) => {
            const isActive = activeId === m.risk.id;
            const dim = isDimmed(m.risk.id);
            return (
              <g
                key={m.risk.id}
                style={{ cursor: "pointer" }}
                opacity={dim ? 0.32 : 1}
                onMouseEnter={(e) => {
                  onHover(m.risk.id);
                  showTip(e);
                }}
                onMouseMove={showTip}
                onMouseLeave={() => onHover(null)}
                onClick={() => onSelect(m.risk)}
              >
                <line
                  x1={m.x}
                  y1={m.yGain}
                  x2={m.x}
                  y2={m.yLoss}
                  stroke={dim ? NEUTRAL : m.color}
                  strokeWidth={isActive && !dim ? 4 : 2.5}
                  strokeLinecap="round"
                  opacity={isActive && !dim ? 1 : 0.85}
                  vectorEffect="non-scaling-stroke"
                />
                {isActive && !dim && (
                  <circle
                    cx={m.x}
                    cy={m.ballY}
                    r={(m.ballR + 5) * zk}
                    fill={m.color}
                    opacity={0.18}
                  />
                )}
                <circle
                  cx={m.x}
                  cy={m.ballY}
                  r={m.ballR * zk}
                  fill={dim ? sphereFill(NEUTRAL) : sphereFill(m.color)}
                  stroke="#fff"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  filter={dim ? undefined : "url(#dotShadow)"}
                />
              </g>
            );
          })}
        </svg>
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1, px: 0.5 }}>
        {[
          { c: ALERT_FILL.red, t: "Signpost breached" },
          { c: ALERT_FILL.amber, t: "Signpost approaching" },
          { c: NEUTRAL, t: "On watch" },
        ].map((l) => (
          <Box key={l.t} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: l.c }} />
            <Typography variant="caption" color="text.secondary">
              {l.t}
            </Typography>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary">
          ● size = consequence · profile column sits under its plan dot · scroll to zoom both
        </Typography>
        {selected && (
          <Typography variant="caption" sx={{ color: "#7b1fa2", fontWeight: 700, ml: "auto" }}>
            Focused on one risk — click it again to show all
          </Typography>
        )}
      </Box>

      {(!risks || risks.length === 0) && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <MDTypography variant="body2" color="text.secondary">
            No active emerging risks to plot yet.
          </MDTypography>
        </Box>
      )}

      {/* Hover mini-tooltip — a quick read without going to the side panel */}
      {tipRisk && tip && (
        <Box
          sx={{
            position: "absolute",
            left: Math.min(tip.x + 14, (containerRef.current?.clientWidth || 600) - 200),
            top: tip.y + 14,
            pointerEvents: "none",
            zIndex: 6,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: "8px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            px: 1.25,
            py: 0.75,
            maxWidth: 220,
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1f2937", lineHeight: 1.3 }}>
            {tipRisk.risk.title}
          </Typography>
          {tipRisk.risk.category && (
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{tipRisk.risk.category}</Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: tipRisk.color }} />
            <Typography sx={{ fontSize: 11, color: "#374151" }}>
              {tipRisk.estVal > 0 ? `~${fmtMoney(tipRisk.estVal)} consequence` : "Qualitative"}
              {tipRisk.risk.signpostAlert === "red"
                ? " · breached"
                : tipRisk.risk.signpostAlert === "amber"
                ? " · approaching"
                : ""}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

DualPlaneRadar.propTypes = {
  risks: PropTypes.arrayOf(PropTypes.object),
  taxonomy: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.string,
  hovered: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  plausibilityMode: PropTypes.string,
  likelihoodBands: PropTypes.arrayOf(PropTypes.string),
  consequenceLevels: PropTypes.arrayOf(PropTypes.string),
};

DualPlaneRadar.defaultProps = {
  risks: [],
  taxonomy: [],
  selected: null,
  hovered: null,
  plausibilityMode: "timeframe_velocity",
  likelihoodBands: [],
  consequenceLevels: [],
};

const DualPlaneRadarWithRef = forwardRef(DualPlaneRadar);
DualPlaneRadarWithRef.defaultProps = DualPlaneRadar.defaultProps;

export default DualPlaneRadarWithRef;
